import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, Timestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';

interface Batch {
  id?: string;
  name: string;
  class: string;
  location: string;
  schedule: string;
  startDate: string;
  maxStudents: number;
  curriculum: string;
  createdAt?: Date;
}

const BatchManagement = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editBatchId, setEditBatchId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<Batch, 'id' | 'createdAt'>>({ 
    name: '',
    class: '',
    location: '',
    schedule: '',
    startDate: '',
    maxStudents: 30,
    curriculum: ''
  });

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'batches'));
      const batchesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Batch[];
      setBatches(batchesData);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'maxStudents' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEdit && editBatchId) {
        const batchRef = doc(db, 'batches', editBatchId);
        await updateDoc(batchRef, {
          ...formData,
        });
      } else {
        await addDoc(collection(db, 'batches'), {
          ...formData,
          createdAt: Timestamp.now(),
        });
      }
      setFormData({ 
        name: '',
        class: '',
        location: '',
        schedule: '',
        startDate: '',
        maxStudents: 30,
        curriculum: ''
      });
      setIsModalOpen(false);
      setIsEdit(false);
      setEditBatchId(null);
      fetchBatches();
    } catch (error) {
      console.error('Error saving batch:', error);
    }
  };

  const handleEdit = (batch: Batch) => {
    setFormData({
      name: batch.name,
      class: batch.class,
      location: batch.location,
      schedule: batch.schedule,
      startDate: batch.startDate,
      maxStudents: batch.maxStudents,
      curriculum: batch.curriculum
    });
    setEditBatchId(batch.id!);
    setIsEdit(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this batch?')) return;
    try {
      await deleteDoc(doc(db, 'batches', id));
      fetchBatches();
    } catch (error) {
      alert('Failed to delete batch.');
      console.error('Error deleting batch:', error);
    }
  };


  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Batch Management</h2>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
        >
          Add New Batch
        </button>
      </div>

      {/* Batch List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="w-full overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max Students</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Curriculum</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {batches.map((batch) => (
              <tr key={batch.id}>
                <td className="px-6 py-4 whitespace-normal break-words">
                  <div className="text-sm font-medium text-gray-900">{batch.name}</div>
                </td>
                <td className="px-6 py-4 whitespace-normal break-words text-sm text-gray-500">
                  {batch.class}
                </td>
                <td className="px-6 py-4 whitespace-normal break-words text-sm text-gray-500">
                  {batch.location}
                </td>
                <td className="px-6 py-4 whitespace-normal break-words text-sm text-gray-500">
                  {batch.schedule}
                </td>
                <td className="px-6 py-4 whitespace-normal break-words text-sm text-gray-500">
                  {new Date(batch.startDate).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-normal break-words text-sm text-gray-500">
                  {batch.maxStudents}
                </td>
                <td className="px-6 py-4 whitespace-normal break-words text-sm text-gray-500">
                  {batch.curriculum}
                </td>
                <td className="px-6 py-4 whitespace-normal break-words text-sm font-medium">
                  <button className="text-indigo-600 hover:text-indigo-900 mr-3" onClick={() => handleEdit(batch)}>Edit</button>
                  <button className="text-red-600 hover:text-red-900" onClick={() => handleDelete(batch.id!)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

      {/* Add Batch Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Add New Batch</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Batch Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Batch Name"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm py-3 focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Class</label>
                <select
                  name="class"
                  value={formData.class}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm py-3 focus:border-blue-500 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Class</option>
                  <option value="IX">IX</option>
                  <option value="X">X</option>
                  <option value="XI">XI</option>
                  <option value="XII">XII</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Location"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm py-3 focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Schedule</label>
                <input
                  type="text"
                  name="schedule"
                  value={formData.schedule}
                  onChange={handleInputChange}
                  placeholder="Schedule (e.g., Mon, Wed, Fri 6-8 PM)"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm py-3 focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  placeholder="Start Date"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm py-3 focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Max Students</label>
                <input
                  type="number"
                  name="maxStudents"
                  value={formData.maxStudents}
                  onChange={handleInputChange}
                  placeholder="Maximum Students"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm py-3 focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Curriculum</label>
                <input
                  type="text"
                  name="curriculum"
                  value={formData.curriculum}
                  onChange={handleInputChange}
                  placeholder="Curriculum"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm py-3 focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Create Batch
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchManagement;
